import type { LogicFilterValue } from '../../fetchSurvey/types/survey';
import { LOGIC_FIELDS } from '../constants/logicFields';
import { LOGIC_OPERATORS, type LogicOperator } from '../constants/logicOperators';
import { buildMatrixCompositeKeys } from '../fields/buildMatrixCompositeKeys';
import { isMatrixRowAnswers } from '../fields/matrixQuestionUtils';
import { parseCompositeKey } from '../fields/parseCompositeKey';
import { toNumber } from '../fields/resolveFieldValue';
import type { LogicContext } from '../types';

function compareRowNumbers(
  actual: number,
  expected: number,
  operator: LogicOperator
): boolean {
  switch (operator) {
    case LOGIC_OPERATORS.GT:
      return actual > expected;
    case LOGIC_OPERATORS.GTE:
      return actual >= expected;
    case LOGIC_OPERATORS.LT:
      return actual < expected;
    case LOGIC_OPERATORS.LTE:
      return actual <= expected;
    case LOGIC_OPERATORS.EQUALS:
      return actual === expected;
    case LOGIC_OPERATORS.NOT_EQUALS:
      return actual !== expected;
    default:
      return false;
  }
}

/**
 * Evaluates skip-logic operators for a matrix composite key (`rowId_suffix`).
 * Returns null when compositeValue is not a composite key — caller uses scalar logic.
 */
export function evaluateCompositeRowFilter(
  field: string | undefined,
  questionId: string | undefined,
  context: LogicContext,
  compositeValue: LogicFilterValue,
  operator: LogicOperator
): boolean | null {
  if (!field || !questionId) return null;

  const parsed = parseCompositeKey(compositeValue);
  if (!parsed) return null;

  const answer = context.answers[questionId];
  if (!isMatrixRowAnswers(answer)) return false;

  if (field === LOGIC_FIELDS.MATRIX_OPTIONS) {
    const keys = buildMatrixCompositeKeys(questionId, answer, context);
    const matches = keys.has(String(compositeValue));
    if (operator === LOGIC_OPERATORS.EQUALS) return matches;
    if (operator === LOGIC_OPERATORS.NOT_EQUALS) return !matches;
    return false;
  }

  if (field === LOGIC_FIELDS.SLIDER) {
    const rowValue = answer[parsed.rowId];
    if (rowValue === undefined || rowValue === null) return false;

    const actual = Array.isArray(rowValue) ? toNumber(rowValue[0]) : toNumber(rowValue);
    const expected = toNumber(parsed.suffix);

    if (actual !== null && expected !== null) {
      return compareRowNumbers(actual, expected, operator);
    }

    if (operator === LOGIC_OPERATORS.EQUALS) return String(rowValue) === parsed.suffix;
    if (operator === LOGIC_OPERATORS.NOT_EQUALS) return String(rowValue) !== parsed.suffix;
    return false;
  }

  return false;
}
